import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from './useAuth'
import { showToast, toastPromise } from '../utils/toast'

export interface PostWithAuthor {
  id: string
  content: string
  category: string
  tags: string[]
  is_anonymous: boolean
  images: string[]
  created_at: string
  post_type: 'text' | 'poll' | 'question'
  poll_options: string[]
  poll_votes_count: number[]
  author: {
    id: string
    full_name: string | null
    avatar_url: string | null
    is_expert: boolean
  } | null
  reactions: Array<{
    id: string
    type: string
    user_id: string
  }>
  comments: Array<{
    id: string
    content: string
    created_at: string
    is_expert_answer: boolean
    parent_id?: string
    author: {
      id: string
      full_name: string | null
      avatar_url: string | null
      is_expert: boolean
    } | null
  }>
  user_vote?: number
}

export function usePosts() {
  const [posts, setPosts] = useState<PostWithAuthor[]>([])
  const [loading, setLoading] = useState(true)
  const { user } = useAuth()

  const fetchPosts = async () => {
    try {
      // Get all posts with authors, reactions, and comments
      const { data, error } = await supabase
        .from('posts')
        .select(`
          *,
          author:profiles!posts_author_id_fkey(id, full_name, avatar_url, is_expert),
          reactions(*),
          comments(
            *,
            author:profiles!comments_author_id_fkey(id, full_name, avatar_url, is_expert)
          )
        `)
        .order('created_at', { ascending: false })

      if (error) throw error
      
      // If user is authenticated, get their poll votes
      let userVotes: Record<string, number> = {};
      if (user) {
        const { data: votesData, error: votesError } = await supabase
          .from('poll_votes')
          .select('post_id, option_index')
          .eq('user_id', user.id)
        
        if (!votesError && votesData) {
          userVotes = votesData.reduce((acc, vote) => {
            acc[vote.post_id] = vote.option_index;
            return acc;
          }, {} as Record<string, number>);
        }
      }
      
      // Add user_vote to posts
      const postsWithVotes = data?.map(post => ({
        ...post,
        user_vote: userVotes[post.id]
      })) || [];
      
      setPosts(postsWithVotes)
    } catch (error) {
      console.error('Error fetching posts:', error)
      showToast.system.error('Failed to load posts')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPosts()
  }, [user])

  const createPost = async (
    content: string, 
    category: string, 
    isAnonymous: boolean, 
    tags: string[] = [], 
    postType: 'text' | 'poll' | 'question' = 'text',
    pollOptions: string[] = []
  ) => {
    if (!user) {
      showToast.auth.authError('Please sign in to create a post')
      return { error: 'Not authenticated' }
    }

    const createPostPromise = async () => {
      // Initialize poll votes count with zeros
      const pollVotesCount = pollOptions.length > 0 ? Array(pollOptions.length).fill(0) : [];
      
      const { data, error } = await supabase
        .from('posts')
        .insert({
          author_id: user.id,
          content,
          category,
          is_anonymous: isAnonymous,
          tags,
          post_type: postType,
          poll_options: pollOptions,
          poll_votes_count: pollVotesCount
        })
        .select()

      if (error) throw error
      
      // Refresh posts
      await fetchPosts()
      return data
    }

    try {
      await toastPromise(createPostPromise(), {
        loading: 'Sharing your thoughts...',
        success: 'Your voice has been shared with the community! 📢',
        error: 'Failed to create post. Please try again.'
      })
      
      return { error: null }
    } catch (error) {
      console.error('Error creating post:', error)
      return { error }
    }
  }

  const addComment = async (postId: string, content: string, parentId?: string) => {
    if (!user) {
      showToast.auth.authError('Please sign in to comment')
      return { error: 'Not authenticated' }
    }

    try {
      const { data, error } = await supabase
        .from('comments')
        .insert({
          post_id: postId,
          author_id: user.id,
          content,
          parent_id: parentId
        })
        .select()

      if (error) throw error
      
      showToast.community.commentAdded()
      
      // Refresh posts to get updated comments
      await fetchPosts()
      return { data, error: null }
    } catch (error) {
      console.error('Error adding comment:', error)
      showToast.system.error('Failed to add comment')
      return { data: null, error }
    }
  }

  const addReaction = async (postId: string, type: string) => {
    if (!user) {
      showToast.auth.authError('Please sign in to react')
      return { error: 'Not authenticated' }
    }

    try {
      // First try to remove existing reaction of same type
      await supabase
        .from('reactions')
        .delete()
        .eq('user_id', user.id)
        .eq('post_id', postId)
        .eq('type', type)

      // Then add new reaction
      const { data, error } = await supabase
        .from('reactions')
        .insert({
          user_id: user.id,
          post_id: postId,
          type
        })
        .select()

      if (error) throw error
      
      showToast.community.reactionAdded(type)
      
      // Refresh posts to get updated reactions
      await fetchPosts()
      return { data, error: null }
    } catch (error) {
      console.error('Error adding reaction:', error)
      showToast.system.error('Failed to add reaction')
      return { data: null, error }
    }
  }

  const voteOnPoll = async (postId: string, optionIndex: number) => {
    if (!user) {
      showToast.auth.authError('Please sign in to vote on polls')
      return { error: 'Not authenticated' }
    }

    try {
      // Check if user has already voted on this poll
      const { data: existingVote, error: checkError } = await supabase
        .from('poll_votes')
        .select('id, option_index')
        .eq('post_id', postId)
        .eq('user_id', user.id)
        .single()

      if (checkError && checkError.code !== 'PGRST116') { // PGRST116 means no rows returned
        throw checkError
      }

      let data
      let error

      if (existingVote) {
        // Update existing vote
        const result = await supabase
          .from('poll_votes')
          .update({ option_index: optionIndex })
          .eq('id', existingVote.id)
          .select()
        
        data = result.data
        error = result.error
        
        if (!error) {
          showToast.community.pollVoteUpdated()
        }
      } else {
        // Insert new vote
        const result = await supabase
          .from('poll_votes')
          .insert({
            post_id: postId,
            user_id: user.id,
            option_index: optionIndex
          })
          .select()
        
        data = result.data
        error = result.error
        
        if (!error) {
          showToast.community.pollVoteAdded()
        }
      }

      if (error) throw error
      
      // Refresh posts to get updated poll votes
      await fetchPosts()
      return { data, error: null }
    } catch (error) {
      console.error('Error voting on poll:', error)
      showToast.system.error('Failed to vote on poll')
      return { data: null, error }
    }
  }

  const getUserVote = async (postId: string) => {
    if (!user) {
      return { data: null, error: 'Not authenticated' }
    }

    try {
      const { data, error } = await supabase
        .from('poll_votes')
        .select('option_index')
        .eq('post_id', postId)
        .eq('user_id', user.id)
        .single()

      if (error && error.code !== 'PGRST116') { // PGRST116 means no rows returned
        throw error
      }

      return { data, error: null }
    } catch (error) {
      console.error('Error getting user vote:', error)
      return { data: null, error }
    }
  }

  return {
    posts,
    loading,
    createPost,
    addComment,
    addReaction,
    voteOnPoll,
    getUserVote,
    refreshPosts: fetchPosts
  }
}